import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import AppContext from "../../../AppContext.jsx";
import { PAGE_STATE, langComponent } from "../../../constants/index.jsx";
import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right-thin.svg";
import useBlogs from "../../../hooks/useBlogs.js";
import StylishButton from "../../common/StylishButton/index.jsx";
import { ReactComponent as PlaySvg } from "../../../assets/icons/play.svg";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import "../../../styles/landingPage/blog.scss";
import _ from "lodash";
import ContentLoader from "react-content-loader";

const assetsURI = import.meta.env.ASSETS_URI;

const BlogItem = ({ desc, cover, onClick, createdOn }) => {
  const { getLang } = useContext(AppContext);
  const TranslationCommon = getLang(langComponent.common);
  const [contentHeight, setContentHeight] = useState(undefined);

  const imageRef = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const _height = entries[0].contentRect.height;
        setContentHeight(_height);
      });
      resizeObserver.observe(node);
    }
  }, []);

  return (
    <div className="lp-blog-item">
      <div className="lp-blog-item-left">
        <img
          ref={imageRef}
          className="lp-blog-item-left-image"
          alt="work"
          src={`${assetsURI}/${cover}`}
        />
      </div>
      <div
        className="lp-blog-item-right"
        style={{ height: contentHeight ?? 0 }}
      >
        <div className="lp-blog-item-right-content">
          <div className="lp-blog-item-right-content__header">
            {format(new Date(createdOn), "dd MMM yyyy")}
          </div>
          <div className="lp-blog-item-right-content__desc">{desc}</div>
          <div className="lp-blog-item-right-content__more" onClick={onClick}>
            <ArrowRightSvg />
            {TranslationCommon("Read")}
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogLoading = () => {
  return (
    <div className="lp-blog-item">
      <div className="lp-blog-item-left">
        <ContentLoader
          className="lp-blog-item-left-image"
          style={{
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <rect x="0" y="0" rx={0} ry={0} width="100%" height="100%" />
        </ContentLoader>
      </div>
      <div className="lp-blog-item-right">
        <div className="lp-blog-item-right-content">
          <ContentLoader
            className="lp-blog-item-right-content__header"
            style={{
              position: "relative",
              height: "20px",
              width: "100px",
            }}
          >
            <rect x="0" y="0" rx={0} ry={0} width="100%" height="100%" />
          </ContentLoader>
          <ContentLoader
            className="lp-blog-item-right-content__desc"
            style={{
              height: "100px",
              width: "150px",
            }}
          >
            <rect x="0" y="0" rx={0} ry={0} width="150px" height="20px" />
            <rect x="0" y="30" rx={0} ry={0} width="150px" height="20px" />
            <rect x="0" y="60" rx={0} ry={0} width="150px" height="20px" />
          </ContentLoader>
          <ContentLoader
            className="lp-blog-item-right-content__desc"
            style={{
              height: "20px",
              width: "150px",
            }}
          >
            <rect x="0" y="0" rx={0} ry={0} width="70px" height="20px" />
          </ContentLoader>
        </div>
      </div>
    </div>
  );
};

const Blog = ({ onSeeMore }) => {
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const { queryBlog } = useBlogs();
  const [items, setItems] = useState([]);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.landingPage);
  const navigate = useNavigate();

  const handleSeeDetail = (_id) => {
    navigate(`/blogs/${_id}`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const { data } = await queryBlog(
      { status: 1, deleted: false },
      1,
      3,
      {
        name: 1,
        desc: 1,
        cover: 1,
        effectiveDate: 1,
        createdOn: 1,
      },
      { createdOn: -1 }
    );

    setItems(data);
    setPageState(PAGE_STATE.IDLE);
  };

  return (
    <div className="lp-blog">
      <div className="lp-blog-header">
        <div className="lp-blog__title">{Translation("Blog")}</div>
        <StylishButton
          label={"See more"}
          icon={<PlaySvg />}
          onClick={onSeeMore}
          size="small"
        />
      </div>
      <div className="lp-blog-content">
        {pageState === PAGE_STATE.LOADING
          ? _.range(1, 4).map((_, index) => (
              <BlogLoading key={`blog-loading-${index}`} />
            ))
          : items.map((m) => {
              return (
                <BlogItem
                  key={`${m.name}_${m.id}`}
                  desc={m.desc}
                  cover={m.cover}
                  effectiveDate={m.effectiveDate}
                  createdOn={m.createdOn}
                  onClick={() => handleSeeDetail(m._id)}
                />
              );
            })}
      </div>
    </div>
  );
};

Blog.propTypes = {
  onSeeMore: PropTypes.func,
};

Blog.defaultProps = {
  onSeeMore: () => {},
};

export default Blog;
